import { Thread } from 'app/messages/message-service/message.model';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { catchError, of, tap } from 'rxjs';
import {
  GetUserSmsThreads,
  GetUserUnknownSms,
  NotifySmsThreadRead, NotifySmsThreadUnread, NotifyUnknownSmsRead,
  NotifyUnknownSmsUnread,
  SetActiveThreadBorrowerId,
  SetActiveUnknownSms, UnsetActiveUserSms, UnsetActiveUnknownSms
} from 'ng2-app/modules/today/state/sms/user-sms.action';
import { SmsService } from 'ng2-app/sms/sms-service/sms.service';
import { DispatchBorrowerContextActions } from 'ng2-app/modules/today/state/user-action-items.action';
import { patch, updateItem } from '@ngxs/store/operators';
import { Sms } from 'ng2-app/sms/sms-service/sms.model';

export class UserSmsThreadsStateModel {
  threads: Array<any>;
  activeThread?: Thread;
  activeThreadBorrowerId?: number;
  activeUnknownSms?: Sms;
  unknownSms: Sms[];
  listLoading: boolean;
  error: any;
  smsTotal: number;
  unknownSmsTotal: number;
  showLoadMore: boolean;
}

@State<UserSmsThreadsStateModel>( {
  name: 'UserSmsThreads',
  defaults: {
    threads: [],
    listLoading: true,
    error: null,
    smsTotal: 0,
    unknownSmsTotal: 0,
    unknownSms: [],
    showLoadMore: false,
  },
} )

@Injectable()
export class UserSmsThreadsState {

  @Selector()
  static listLoading( state: UserSmsThreadsStateModel ) {
    return state.listLoading;
  }

  @Selector()
  static activeThread( state: UserSmsThreadsStateModel ) {
    return state.activeThread || null;
  }

  @Selector()
  static activeThreadBorrowerId( state: UserSmsThreadsStateModel ) {
    return state.activeThreadBorrowerId || null;
  }

  @Selector()
  static threads( state: UserSmsThreadsStateModel ) {
    return state.threads;
  }

  @Selector()
  static unknownSms( state: UserSmsThreadsStateModel ) {
    return state.unknownSms;
  }

  @Selector()
  static activeUnknownSms( state: UserSmsThreadsStateModel ) {
    return state.activeUnknownSms;
  }

  @Selector()
  static smsTotal( state: UserSmsThreadsStateModel ) {
    return state.smsTotal;
  }

  @Selector()
  static unknownSmsTotal( state: UserSmsThreadsStateModel ) {
    return state.unknownSmsTotal;
  }

  @Selector()
  static showLoadMore( state: UserSmsThreadsStateModel ) {
    return state.showLoadMore;
  }

  constructor(
    private store: Store,
    private smsService: SmsService,
  ) {}

  @Action( GetUserSmsThreads )
  getUserSmsThreads(
    { getState, patchState }: StateContext<UserSmsThreadsStateModel>,
    { lenderUserId, reset, page, filterBy, sortDirection }
  ) {
    const current = getState();

    const prePatch = {
      threads: current.threads,
      listLoading: true,
    };

    if ( reset ) {
      prePatch.threads = [];
    }

    patchState( prePatch );

    return this.smsService
      .getLenderUserSms( lenderUserId, page, filterBy, sortDirection )
      .pipe(
        tap( ( response ) => {
          const currentThreads = getState().threads;
          let threads = response.data;
          threads = reset ? threads :
            sortDirection === 'desc' ? [ ...currentThreads, ...threads ] :
              [ ...threads, ...currentThreads ];

          const firstThread = threads[0],
            smsTotal = response.total || 0;
            const showLoadMore: boolean = smsTotal > 0 && threads.length < smsTotal;

          patchState( {
            threads,
            smsTotal: response.total,
            listLoading: false,
            showLoadMore,
          } );

          if ( reset && firstThread && firstThread.borrowerId ) {
            this.store.dispatch( new SetActiveThreadBorrowerId( firstThread.borrowerId ) );
          }

        } ),
        catchError( ( error ) => {
          patchState( {
            listLoading: false,
            error,
          } );
          return of( '' );
        } )
      );
  }

  @Action(GetUserUnknownSms)
  getUserUnknownSms(
    { getState, patchState }: StateContext<UserSmsThreadsStateModel>,
    { lenderUserId, reset, page, sortDirection }: GetUserUnknownSms
  ) {
    const current = getState();

    const prePatch = {
      unknownSms: current.unknownSms,
      listLoading: true,
    };

    if ( reset ) {
      prePatch.unknownSms = [];
    }

    patchState( prePatch );

    return this.smsService
      .getLenderUserUnknownSms( lenderUserId, page, sortDirection )
      .pipe(
        tap( ( response ) => {
          let unknownSms = response.data;
          const currentUnknownSms = current.unknownSms;
          unknownSms = reset ? unknownSms :
            sortDirection === 'desc' ? [ ...currentUnknownSms, ...unknownSms ] :
              [ ...unknownSms, ...currentUnknownSms ];

          const first = unknownSms[0],
            unknownSmsTotal = response.total || 0;
          const showLoadMore: boolean = unknownSmsTotal > 0 && unknownSms.length < unknownSmsTotal;

          patchState( {
            unknownSms,
            unknownSmsTotal,
            listLoading: false,
            activeUnknownSms: (first && reset) ? first : current.activeUnknownSms,
            showLoadMore,
          } );

        } ),
        catchError( ( error ) => {
          patchState( {
            unknownSms: [],
            listLoading: false,
            error,
          } );
          return of( '' );
        } )
      );
  }

  @Action(SetActiveThreadBorrowerId)
  setActiveThreadBorrowerId(
    { getState, patchState }: StateContext<UserSmsThreadsStateModel>,
    { newBorrowerId, filterUnread }: SetActiveThreadBorrowerId
  ) {
    newBorrowerId ? this.store.dispatch( new DispatchBorrowerContextActions(newBorrowerId)) : false;
    const patchVal: any = {
      activeThreadBorrowerId: newBorrowerId
    };
    if(filterUnread) {
      patchVal.threads = getState().threads.filter( thread => thread.unreadMessageCount );
    }
    patchState(patchVal);
  }

  @Action(SetActiveUnknownSms)
  setActiveUnknownSms(
    { getState, patchState }: StateContext<UserSmsThreadsStateModel>,
    { sms }: SetActiveUnknownSms
  ) {
    patchState({
      activeUnknownSms: sms
    });
  }

  @Action(NotifySmsThreadRead)
  notifySmsThreadRead(
    { setState }: StateContext<UserSmsThreadsStateModel>,
    { borrowerId }: NotifySmsThreadRead
  ) {
    setState(
      patch({
        threads: updateItem( (thread: Thread) => thread.borrowerId === borrowerId, patch({
          unreadMessageCount: 0
        }))
      })
    );
  }

  @Action(NotifySmsThreadUnread)
  notifySmsThreadUnread(
    { setState }: StateContext<UserSmsThreadsStateModel>,
    { borrowerId, unreadCount }: NotifySmsThreadUnread
  ) {
    setState(
      patch({
        threads: updateItem( (thread: Thread) => thread.borrowerId === borrowerId, patch({
          unreadMessageCount: unreadCount
        }))
      })
    );
  }

  @Action(NotifyUnknownSmsRead)
  notifyUnknownSmsRead(
    { setState }: StateContext<UserSmsThreadsStateModel>,
    { smsId }: NotifyUnknownSmsRead
  ) {
    setState(
      patch({
        unknownSms: updateItem( (sms: Sms) => sms.id === smsId, patch({
          read: new Date().toString()
        }))
      })
    );
  }

  @Action(NotifyUnknownSmsUnread)
  notifyUnknownSmsUnread(
    { getState, patchState }: StateContext<UserSmsThreadsStateModel>
  ) {
    const unknownSms = getState().unknownSms;
    unknownSms[0].read = undefined;
    patchState({unknownSms: unknownSms});
  }

  @Action(UnsetActiveUserSms)
  unsetActiveUserSms(
    { patchState }: StateContext<UserSmsThreadsStateModel>
  ) {
    patchState({
      activeThread: undefined,
      activeThreadBorrowerId: undefined,
    });
  }

  @Action(UnsetActiveUnknownSms)
  unsetActiveUnknownSms(
    { patchState }: StateContext<UserSmsThreadsStateModel>
  ) {
    patchState({
      activeUnknownSms: undefined,
    });
  }

}
